import { client, clientNoCdn } from '../sanity'
import type { SupplierStockItem } from '@/types/supplierStockItem'
import type { CategoryType } from '@/types/categoryType'

export interface PackageDetails {
  length?: number
  width?: number
  height?: number
  weight?: number
  dimensionUnit?: 'mm' | 'cm' | 'in' | 'm'
  weightUnit?: 'g' | 'kg' | 'lb' | 'oz'
}

export interface StockSupplier {
  _id: string
  name: string
  url?: string
  stockSupplierSource?: {
    _id: string
    name: string
    rate?: number
  }
  flags?: {
    isFavourite?: boolean
  }
}

export interface StockItem {
  _id: string
  _type: 'stockItem'
  sku: string
  name: string
  description?: string
  supplierItem?: {
    _ref: string
    _type: 'reference'
  } | SupplierStockItem // Reference to supplierItem or populated object
  supplier?: StockSupplier // Supplier that contains this supplierItem (via reverse lookup)
  category?: {
    _ref: string
    _type: 'reference'
  } | CategoryType // Reference to category or populated object
  packageDetails?: PackageDetails // Nested package details object
  images?: string[]
  attributes?: Record<string, any>
  _createdAt?: string
  _updatedAt?: string
}

/**
 * Fetch all stock items
 */
export const getAllStockItems = async (): Promise<StockItem[]> => {
  return client.fetch(
    `*[_type == "stockItem"] | order(_updatedAt desc) {
      _id,
      _type,
      sku,
      name,
      description,
      "supplierItemId": supplierItem._ref,
      supplierItem-> {
        _id,
        _type,
        name,
        sku,
        description,
        category-> {
          _id,
          _type,
          name,
          code
        },
        quantityPerUnit,
        unit-> {
          _id,
          name
        },
        brand,
        manufacturer,
        reorderLevel,
        reorderQuantity,
        taxPercentage,
        colour-> {
          _id,
          name
        },
        shape-> {
          _id,
          name
        },
        length,
        width
      },
      "supplier": *[_type == "stockSupplier" && ^.supplierItemId in stockSupplierItems[]._ref][0] {
        _id,
        name,
        url,
        stockSupplierSource-> {
          _id,
          name,
          rate
        },
        flags {
          isFavourite
        }
      },
      category-> {
        _id,
        _type,
        name,
        code
      },
      packageDetails {
        length,
        width,
        height,
        weight,
        dimensionUnit,
        weightUnit
      },
      images,
      attributes,
      _createdAt,
      _updatedAt
    }`
  ).then((items) =>
    items.map((item: any) => {
      // Helper to check if something is a reference object
      const isReferenceObject = (obj: any): boolean => {
        return (
          obj !== null &&
          typeof obj === 'object' &&
          '_ref' in obj &&
          '_type' in obj &&
          obj._type === 'reference'
        )
      }
      
      // Normalize supplierItem: convert reference objects to null
      let normalizedSupplierItem = item.supplierItem
      if (normalizedSupplierItem) {
        if (isReferenceObject(normalizedSupplierItem) || 
            (typeof normalizedSupplierItem === 'object' &&
             normalizedSupplierItem !== null &&
             '_ref' in normalizedSupplierItem &&
             !('supplier' in normalizedSupplierItem))) {
          // It's a reference object, not populated
          normalizedSupplierItem = null
        }
      }
      
      // Normalize category: preserve reference format for Edit form compatibility
      // The Edit form needs {_ref, _type: 'reference'} format to work properly
      let normalizedCategory: any = item.category
      if (normalizedCategory) {
        if (isReferenceObject(normalizedCategory)) {
          // It's a reference object - preserve it for Edit form
          normalizedCategory = {
            _ref: normalizedCategory._ref,
            _type: 'reference' as const,
          }
        } else if (typeof normalizedCategory === 'object' &&
                   normalizedCategory !== null) {
          if ('_id' in normalizedCategory && 'name' in normalizedCategory) {
            // Category is populated - convert to reference format for Edit form
            // But keep the populated data for display purposes
            normalizedCategory = {
              ...normalizedCategory, // Keep populated data for display
              _ref: normalizedCategory._id,
              _type: 'reference' as const,
            }
          } else if ('_ref' in normalizedCategory && !('name' in normalizedCategory)) {
            // It's a reference object without _type - ensure it has _type
            normalizedCategory = {
              _ref: (normalizedCategory as any)._ref,
              _type: 'reference' as const,
            }
          }
        }
      }
      
      // Normalize all other fields that might be reference objects
      const normalizedItem: any = {
        ...item,
        supplierItem: normalizedSupplierItem,
        category: normalizedCategory,
      }
      
      // Recursively check attributes for reference objects
      if (normalizedItem.attributes && typeof normalizedItem.attributes === 'object') {
        const normalizedAttributes: Record<string, any> = {}
        for (const [key, value] of Object.entries(normalizedItem.attributes)) {
          if (isReferenceObject(value)) {
            normalizedAttributes[key] = null
          } else {
            normalizedAttributes[key] = value
          }
        }
        normalizedItem.attributes = normalizedAttributes
      }
      
      return normalizedItem
    })
  )
}

/**
 * Fetch a single stock item by ID
 */
export const getStockItemById = async (id: string, useFreshData: boolean = false): Promise<StockItem | null> => {
  // Use non-CDN client for fresh data (e.g., after updates) to bypass CDN caching
  const queryClient = useFreshData ? clientNoCdn : client
  
  return queryClient
    .fetch(
      `*[_type == "stockItem" && _id == $id][0] {
      _id,
      _type,
      sku,
      name,
      description,
      "supplierItemId": supplierItem._ref,
      supplierItem-> {
        _id,
        _type,
        name,
        sku,
        description,
        category-> {
          _id,
          _type,
          name,
          code
        },
        quantityPerUnit,
        unit-> {
          _id,
          name
        },
        brand,
        manufacturer,
        reorderLevel,
        reorderQuantity,
        taxPercentage,
        colour-> {
          _id,
          name
        },
        shape-> {
          _id,
          name
        },
        length,
        width
      },
      "supplier": *[_type == "stockSupplier" && ^.supplierItemId in stockSupplierItems[]._ref][0] {
        _id,
        name,
        url,
        stockSupplierSource-> {
          _id,
          name,
          rate
        },
        flags {
          isFavourite
        }
      },
      category-> {
        _id,
        _type,
        name,
        code
      },
      packageDetails {
        length,
        width,
        height,
        weight,
        dimensionUnit,
        weightUnit
      },
      images,
      attributes,
      _createdAt,
      _updatedAt
    }`,
      { id }
    )
    .then((item: any) => {
      if (!item) return null
      
      // Debug: Log raw item data to verify package details are fetched
      if (process.env.NODE_ENV === 'development') {
        console.log('Raw StockItem from Sanity:', {
          id: item._id,
          packageDetails: item.packageDetails,
          packageDetailsRaw: JSON.stringify(item.packageDetails),
          packageDetailsKeys: item.packageDetails ? Object.keys(item.packageDetails) : null,
          category: item.category,
          categoryType: typeof item.category,
          categoryIsObject: item.category && typeof item.category === 'object',
          categoryKeys: item.category ? Object.keys(item.category) : null,
          allFields: Object.keys(item),
        })
      }
      
      // Helper to check if something is a reference object
      const isReferenceObject = (obj: any): boolean => {
        return (
          obj !== null &&
          typeof obj === 'object' &&
          '_ref' in obj &&
          '_type' in obj &&
          obj._type === 'reference'
        )
      }
      
      // Normalize supplierItem: convert reference objects to null
      let normalizedSupplierItem = item.supplierItem
      if (normalizedSupplierItem) {
        if (isReferenceObject(normalizedSupplierItem) || 
            (typeof normalizedSupplierItem === 'object' &&
             normalizedSupplierItem !== null &&
             '_ref' in normalizedSupplierItem &&
             !('supplier' in normalizedSupplierItem))) {
          // It's a reference object, not populated
          normalizedSupplierItem = null
        }
      }
      
      // Normalize category: preserve reference format for Edit form compatibility
      // The Edit form needs {_ref, _type: 'reference'} format to work properly
      let normalizedCategory: any = item.category
      if (normalizedCategory) {
        if (isReferenceObject(normalizedCategory)) {
          // It's a reference object - preserve it for Edit form
          normalizedCategory = {
            _ref: normalizedCategory._ref,
            _type: 'reference' as const,
          }
        } else if (typeof normalizedCategory === 'object' &&
                   normalizedCategory !== null) {
          if ('_id' in normalizedCategory && 'name' in normalizedCategory) {
            // Category is populated - convert to reference format for Edit form
            // But keep the populated data for display purposes
            normalizedCategory = {
              ...normalizedCategory, // Keep populated data for display
              _ref: normalizedCategory._id,
              _type: 'reference' as const,
            }
          } else if ('_ref' in normalizedCategory && !('name' in normalizedCategory)) {
            // It's a reference object without _type - ensure it has _type
            normalizedCategory = {
              _ref: (normalizedCategory as any)._ref,
              _type: 'reference' as const,
            }
          }
        }
      }
      
      // Normalize all other fields that might be reference objects
      // Explicitly preserve package details to ensure they're not lost
      const normalizedItem: any = {
        ...item,
        supplierItem: normalizedSupplierItem,
        category: normalizedCategory,
        // Remove temporary supplierItemId field used for reverse lookup
        supplierItemId: undefined,
        // Explicitly preserve package details object
        packageDetails: item.packageDetails ? {
          length: item.packageDetails.length !== undefined && item.packageDetails.length !== null ? Number(item.packageDetails.length) : undefined,
          width: item.packageDetails.width !== undefined && item.packageDetails.width !== null ? Number(item.packageDetails.width) : undefined,
          height: item.packageDetails.height !== undefined && item.packageDetails.height !== null ? Number(item.packageDetails.height) : undefined,
          weight: item.packageDetails.weight !== undefined && item.packageDetails.weight !== null ? Number(item.packageDetails.weight) : undefined,
          dimensionUnit: item.packageDetails.dimensionUnit || undefined,
          weightUnit: item.packageDetails.weightUnit || undefined,
        } : undefined,
      }
      
      // Recursively check attributes for reference objects
      if (normalizedItem.attributes && typeof normalizedItem.attributes === 'object') {
        const normalizedAttributes: Record<string, any> = {}
        for (const [key, value] of Object.entries(normalizedItem.attributes)) {
          if (isReferenceObject(value)) {
            normalizedAttributes[key] = null
          } else {
            normalizedAttributes[key] = value
          }
        }
        normalizedItem.attributes = normalizedAttributes
      }
      
      // Debug: Log normalized item to verify package details are preserved
      if (process.env.NODE_ENV === 'development') {
        console.log('Normalized StockItem:', {
          id: normalizedItem._id,
          packageDetails: normalizedItem.packageDetails,
          packageDetailsRaw: JSON.stringify(normalizedItem.packageDetails),
          packageDetailsKeys: normalizedItem.packageDetails ? Object.keys(normalizedItem.packageDetails) : null,
          category: normalizedItem.category,
          categoryType: typeof normalizedItem.category,
          categoryIsObject: normalizedItem.category && typeof normalizedItem.category === 'object',
          categoryRef: normalizedItem.category?._ref,
          categoryId: normalizedItem.category?._id,
          categoryName: normalizedItem.category?.name,
          categoryKeys: normalizedItem.category ? Object.keys(normalizedItem.category) : null,
        })
      }
      
      return normalizedItem
    })
}
